import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { Autocomplete } from '../../input/Autocomplete/Autocomplete';
import { TextField } from '../../input/TextField/TextField';
import styles from './FilterContent.module.scss';
import { FilterContentFieldDataTypes } from '../constants';

interface FilterWithOperatorProps {
  filterText: string;
  filterDataType: FilterContentFieldDataTypes;
  fieldProps: Record<string, unknown>;
  options: { label: string; value: string }[];
  validationFn?: (value: FilterContentFieldDataTypes) => boolean;
  value: { operator: string; value: FilterContentFieldDataTypes } | undefined;
  onChange: (value: { operator: string; value: FilterContentFieldDataTypes }) => void;
}

export function FilterWithOperator({
  filterText,
  filterDataType,
  fieldProps,
  value,
  options,
  validationFn,
  onChange
}: FilterWithOperatorProps) {
  const [operator, setOperator] = useState<{ label: string; value: string }>();
  const [filterValue, setFilterValue] = useState<FilterContentFieldDataTypes | null>(null);

  useEffect(() => {
    if (value) {
      const { operator: _operator, value: _value } = value;
      const selectedOperator = options.find(option => option.value === _operator);

      if (selectedOperator) {
        setOperator({ ...selectedOperator });
      }

      setFilterValue(_value);
    } else {
      setOperator(options[0]);
      setFilterValue(null);
    }
  }, [value]);

  const onOperatorChange = (fieldValue: { label: string; value: string }) => {
    setOperator({ ...fieldValue });

    if (fieldValue && filterValue) {
      onChange({
        operator: fieldValue.value,
        value: filterValue
      });
    }
  };

  const onFieldChange = (fieldValue: FilterContentFieldDataTypes) => {
    if (fieldValue && validationFn && !validationFn(fieldValue)) {
      return;
    }

    setFilterValue(fieldValue);

    if (operator && 'value' in operator) {
      onChange({
        operator: operator.value,
        value: fieldValue
      });
    }
  };

  return (
    <div className='center-flex-row px-4 mt-3 justify-between'>
      <p className='text-nowrap text-body-1'>{filterText} </p>

      <div className='center-flex-row ml-4'>
        <Autocomplete
          className={styles.operatorDropdown}
          dataId={`${filterDataType}-operator-autocomplete`}
          fullWidth
          options={options}
          isOptionEqualToValue={(option, selectedValue) =>
            (option as { label: string }).label === (selectedValue as { label: string }).label
          }
          value={operator ?? options[0]}
          onChange={(_, _value) => onOperatorChange(_value as { label: string; value: string })}
          disableClearable
        />

        <TextField
          {...fieldProps}
          dataId={filterText}
          onChange={event => onFieldChange(event.target.value as FilterContentFieldDataTypes)}
          value={filterValue}
          type={filterDataType}
          className={clsx('ml-2', styles.filterField)}
          variant='outlined'
        />
      </div>
    </div>
  );
}
